/** Identificador único de evento en el bus normalizado de Modo Vivo. */
export type EventId = string;

/** Todos los eventos de Orden 001 son simulados hasta conectar conectores externos. */
export type SimulatedFlag = true;

export type ModoVivoEventType =
  | 'customer.arrival'
  | 'table.open'
  | 'table.assign'
  | 'order.register'
  | 'order.send_kitchen'
  | 'order.preparing'
  | 'order.ready'
  | 'order.deliver'
  | 'bill.request'
  | 'payment.collect'
  | 'customer.leave'
  | 'table.release'
  | 'supplier.receive'
  | 'stock.update';

export interface ModoVivoEventBase<T extends ModoVivoEventType, P> {
  id: EventId;
  type: T;
  timestamp: number;
  simulated: SimulatedFlag;
  payload: P;
}

export interface CustomerArrivalPayload {
  customerId: string;
  partySize: number;
  targetTableNumber?: number;
}

export interface TableOpenPayload {
  tableNumber: number;
}

export interface TableAssignPayload {
  tableNumber: number;
  waiterId: string;
}

export interface OrderRegisterPayload {
  tableNumber: number;
  items: Array<{ name: string; quantity: number; unitPrice: number }>;
}

export interface OrderKitchenPayload {
  tableNumber: number;
  orderId: string;
}

export interface OrderPreparingPayload {
  orderId: string;
}

export interface OrderReadyPayload {
  orderId: string;
}

export interface OrderDeliverPayload {
  orderId: string;
  waiterId: string;
}

export interface BillRequestPayload {
  tableNumber: number;
}

export interface PaymentCollectPayload {
  tableNumber: number;
  amount: number;
  method: 'efectivo' | 'tarjeta' | 'transferencia' | 'qr';
}

export interface CustomerLeavePayload {
  tableNumber: number;
  customerIds: string[];
}

export interface TableReleasePayload {
  tableNumber: number;
}

export interface SupplierReceivePayload {
  supplierName: string;
  items: Array<{ sku: string; name: string; quantity: number }>;
}

export interface StockUpdatePayload {
  sku: string;
  quantityDelta: number;
}

export type ModoVivoEvent =
  | ModoVivoEventBase<'customer.arrival', CustomerArrivalPayload>
  | ModoVivoEventBase<'table.open', TableOpenPayload>
  | ModoVivoEventBase<'table.assign', TableAssignPayload>
  | ModoVivoEventBase<'order.register', OrderRegisterPayload>
  | ModoVivoEventBase<'order.send_kitchen', OrderKitchenPayload>
  | ModoVivoEventBase<'order.preparing', OrderPreparingPayload>
  | ModoVivoEventBase<'order.ready', OrderReadyPayload>
  | ModoVivoEventBase<'order.deliver', OrderDeliverPayload>
  | ModoVivoEventBase<'bill.request', BillRequestPayload>
  | ModoVivoEventBase<'payment.collect', PaymentCollectPayload>
  | ModoVivoEventBase<'customer.leave', CustomerLeavePayload>
  | ModoVivoEventBase<'table.release', TableReleasePayload>
  | ModoVivoEventBase<'supplier.receive', SupplierReceivePayload>
  | ModoVivoEventBase<'stock.update', StockUpdatePayload>;

export function createEventId(prefix = 'evt'): EventId {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createSimulatedEvent<T extends ModoVivoEventType, P>(
  type: T,
  payload: P,
): ModoVivoEventBase<T, P> {
  return {
    id: createEventId(),
    type,
    timestamp: Date.now(),
    simulated: true,
    payload,
  };
}
